/**
 * Display formatting.
 *
 * The staff surface is Vietnamese-only by product decision, so everything here
 * defaults to `vi-VN`. The CUSTOMER surface must not use these helpers: it
 * formats through `@sonauto/i18n` with the guest's negotiated locale and the
 * BRANCH's currency (see `src/customer/CustomerI18nProvider.tsx`).
 */

import { formatMoney, money } from '@sonauto/utilities';

/** Money for staff eyes. `exponent` comes from the branch, never guessed. */
export function vnd(amountMinor: number, currency = 'VND', exponent = 0): string {
  if (!Number.isFinite(amountMinor)) return '—';
  return formatMoney(money(Math.round(amountMinor), currency, exponent), 'vi-VN');
}

/** `mm:ss`, and `-mm:ss` once a ticket is past due. Used all over the KDS. */
export function clock(totalSeconds: number): string {
  const negative = totalSeconds < 0;
  const abs = Math.abs(Math.round(totalSeconds));
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${negative ? '-' : ''}${m}:${String(s).padStart(2, '0')}`;
}

/** `14:32` in the branch's local reading. */
export function hhmm(at: number | null | undefined): string {
  if (at === null || at === undefined || !Number.isFinite(at)) return '—';
  return new Intl.DateTimeFormat('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false }).format(at);
}

/** `14:32:05 20/07`. */
export function stamp(at: number | null | undefined): string {
  if (at === null || at === undefined || !Number.isFinite(at)) return '—';
  return new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    day: '2-digit',
    month: '2-digit',
    hour12: false,
  }).format(at);
}

/** "3 phút trước" — coarse on purpose; a POS never needs second precision here. */
export function ago(at: number | null | undefined, now = Date.now()): string {
  if (at === null || at === undefined) return '—';
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 45) return 'vừa xong';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} phút trước`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} giờ trước`;
  return `${Math.round(hours / 24)} ngày trước`;
}

/** `YYYY-MM-DD` for the device's day. The API's business date may differ. */
export function todayIso(at = Date.now()): string {
  const d = new Date(at);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Vietnamese label for a spice level 0–5. */
export function spiceLabel(level: number | null | undefined): string | null {
  switch (level) {
    case 0:
      return 'Không cay';
    case 1:
      return 'Cay nhẹ';
    case 2:
      return 'Cay vừa';
    case 3:
      return 'Cay';
    case 4:
      return 'Rất cay';
    case 5:
      return 'Siêu cay';
    default:
      return null;
  }
}

/**
 * `modifiers_snapshot_json` is `unknown[]` on the wire. This is the tolerant
 * reader: anything that is not a `{ name }`-ish object is skipped rather than
 * rendered as `[object Object]` on a chef's screen.
 */
export function readModifierNames(raw: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name : typeof record.code === 'string' ? record.code : null;
    if (name === null || name === '') continue;
    const qty = typeof record.quantity === 'number' && record.quantity > 1 ? ` ×${record.quantity}` : '';
    out.push(`${name}${qty}`);
  }
  return out;
}

/** Allergen flags arrive as an opaque JSON array; read the codes defensively. */
export function readAllergenCodes(raw: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string' && entry !== '') {
      out.push(entry);
      continue;
    }
    if (entry !== null && typeof entry === 'object') {
      const record = entry as Record<string, unknown>;
      if (typeof record.code === 'string' && record.code !== '') out.push(record.code);
    }
  }
  return out;
}
